import type { JSX } from 'react';
import { AlertTriangle, CircleDashed, Pause, TrendingUp, XCircle, type LucideIcon } from 'lucide-react';
import { Pill, type PillTone } from '@/components/ui/pill';
import type { RepeatSchedule, RunStatus, ScheduleHealth } from '../api/types';
import { healthHint, healthLabel } from './model';

const HEALTH_STYLE: Record<ScheduleHealth, { tone: PillTone; icon: LucideIcon }> = {
  earning: { tone: 'success', icon: TrendingUp },
  dry: { tone: 'pending', icon: AlertTriangle },
  failing: { tone: 'failed', icon: XCircle },
  paused: { tone: 'muted', icon: Pause },
  new: { tone: 'muted', icon: CircleDashed },
};

/** Whether a schedule is still worth its budget — a derived state, so a Pill. */
export function HealthPill({
  schedule,
}: {
  schedule: Pick<RepeatSchedule, 'health' | 'dryRuns' | 'pausedByArchive'> &
    Partial<Pick<RepeatSchedule, 'enabled' | 'pausedReason' | 'pausedAfterDryRuns'>>;
}): JSX.Element {
  // Paused for being dry is a warning to act on, not a neutral "switched off".
  const autoPaused = schedule.enabled === false && schedule.pausedReason === 'dry';
  const style = autoPaused ? { tone: 'pending' as PillTone, icon: Pause } : HEALTH_STYLE[schedule.health] ?? HEALTH_STYLE.new;
  return (
    <span title={healthHint(schedule)} data-health={schedule.health} data-paused-reason={autoPaused ? 'dry' : undefined}>
      <Pill tone={style.tone} icon={style.icon}>
        {healthLabel(schedule)}
      </Pill>
    </span>
  );
}

const RUN_STYLE: Record<RunStatus, { tone: PillTone; label: string }> = {
  done: { tone: 'success', label: 'Finished' },
  running: { tone: 'info', label: 'Running' },
  stopped: { tone: 'muted', label: 'Stopped' },
  failed: { tone: 'failed', label: 'Failed' },
};

export function RunStatusPill({ status }: { status: RunStatus }): JSX.Element {
  const style = RUN_STYLE[status] ?? RUN_STYLE.done;
  return <Pill tone={style.tone}>{style.label}</Pill>;
}
